// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useState } from "react";
import { Alert, Button, Modal, Tooltip } from "antd";
import { BlockOutlined, MobileOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { NewsThemeInfoResponseDTO } from "../../../../../api/backend-openapi";
import i18n from "i18next";
import { useStore } from "effector-react";
import { NewsThemeStore } from "../../../../../state/newsTheme/NewsThemeStore";
import { TemplateStore } from "../../../../../state/template/TemplateStore";
import { Block } from "../../../../types/NewsTypes";
import { TemplateHtmlResult } from "../../../../types/TemplateTypes";
import { TemplateDTO } from "../../../../../utils/htmlTemplate/dto/template.dto";
import HtmlTemplateService from "../../../../../utils/htmlTemplate/HtmlTemplateService";

export interface NewsEditionPreviewButtonProps {
  blocks: Block[];
  newsTitle?: string;
  newsImage: string;
  newsSummary?: string;
  iconButton: boolean;
  newsThemeId?: string;
  newsEventStartDate?: string;
  newsEventEndDate?: string;
  color?: string;
}

const NewsEditionPreviewButton: React.FunctionComponent<NewsEditionPreviewButtonProps> = (
  props: NewsEditionPreviewButtonProps
) => {
  const {
    blocks,
    iconButton,
    newsThemeId,
    newsTitle,
    newsImage,
    newsSummary,
    newsEventStartDate,
    newsEventEndDate
  } = props;

  const newsThemeStore: NewsThemeInfoResponseDTO[] | null = useStore(
    NewsThemeStore
  );

  const templatesStore: TemplateDTO[] | null = useStore(TemplateStore);

  const newsTheme = newsThemeStore.find(n => n.id === newsThemeId);

  const { t } = useTranslation();

  const [visible, setVisible] = useState<boolean>(false);

  const disabled = () => {
    return (
      !newsTitle || !newsSummary || newsImage === undefined || blocks.length < 1
    );
  };

  const getTemplateResult = (headTemplate: string): TemplateHtmlResult => {
    try {
      const htmlResult = HtmlTemplateService.generateContentHtmlComplete(
        blocks,
        newsTitle as string,
        newsImage,
        newsSummary as string,
        headTemplate,
        templatesStore,
        newsEventStartDate,
        newsEventEndDate,
        newsTheme?.color
      );
      return {
        isSuccess: true,
        result: htmlResult
      };
    } catch (error) {
      console.error(
        "Cannot create HTML preview",
        (error as { message?: string })?.message || error
      );
      return {
        isSuccess: false,
        error
      };
    }
  };

  const newsHeadTemplate = `<div>  
    <div class="newsTitle"> 
    {{#if (eq inputs.0.inputType "text")}} <strong>{{{inputs.0.content}}}</strong>{{/if}}
    </div>
    <div>
      <div class="newsMainImage">
        <img alt="image" {{#if (eq inputs.1.inputType "img")}}src={{{inputs.1.content}}} {{/if}} />
      </div>
      {{#if (eq inputs.3.inputType "text")}}
      <div class="newsEventDescription" style="color:{{inputs.2.content}}">
        Date de l'événement : Du 
        {{#if (eq inputs.3.inputType "text")}}
        {{{inputs.3.content}}}
        {{/if}}
         au 
        {{#if (eq inputs.4.inputType "text")}}
        {{{inputs.4.content}}}
        {{/if}}
      </div>
      <div class="newsEventAddButton" style="color:{{inputs.2.content}}">
        <span class="material-icons">event</span>
        <strong>Ajouter l'événement à mon agenda</strong>
      </div>
      {{/if}}
      <div class="newsSummary">
        {{#if (eq inputs.5.inputType "text")}}{{{breaklines inputs.5.content}}}{{/if}} 
      </div>
    </div>
  </div>`;

  const result: TemplateHtmlResult = !disabled()
    ? getTemplateResult(newsHeadTemplate)
    : {
        isSuccess: false
      };

  const innerButton = iconButton ? (
    <Tooltip
      title={i18n.t("news_list.table.tooltip.preview")}
      mouseEnterDelay={0.2}
      placement="bottom"
    >
      <Button
        type="default"
        icon={<MobileOutlined />}
        size="large"
        className="btn-secondary mr-1 btn-list"
        onClick={() => setVisible(true)}
      />
    </Tooltip>
  ) : (
    <Button
      disabled={disabled()}
      type="default"
      className="btn-secondary m-2"
      onClick={() => setVisible(true)}
    >
      <MobileOutlined />
      {t("detail_news.preview")}
    </Button>
  );

  return (
    <>
      {innerButton}
      <Modal
        className="news-preview"
        title={t("detail_news.preview")}
        visible={visible}
        centered
        footer={
          <Button key="closePreviewModal" onClick={() => setVisible(false)}>
            {t("global.close")}
          </Button>
        }
        onCancel={() => setVisible(false)}
      >
        <div
          className="preview-theme d-flex flex-row font-xs align-items-center"
          style={{ backgroundColor: newsTheme?.color }}
        >
          <BlockOutlined />
          <div className="ml-2">{newsTheme?.name}</div>
        </div>
        {!disabled() &&
          (result.isSuccess && result.result ? (
            <div
              dangerouslySetInnerHTML={{
                // eslint-disable-next-line @typescript-eslint/naming-convention
                __html: result.result
              }}
            />
          ) : (
            <Alert
              message={t("global.error.title")}
              description={
                t("template.error") +
                (result ? " \n: " + result.error?.message || result.error : "")
              }
              type="error"
              showIcon
            />
          ))}
      </Modal>
    </>
  );
};

export default NewsEditionPreviewButton;
